import { useEffect, useState } from 'react';
import { Table, Typography, Input, Alert, Empty, Spin } from 'antd';
import { SearchOutlined, CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { useClient } from '../../context/ClientContext';

const { Title, Text } = Typography;

const fmtFecha = (f) => new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtMoneda = (v) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v);

export const MisPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [q, setQ]             = useState('');
  const navigate  = useNavigate();
  const { clientId , clientPath} = useClient();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.pedidos.getMisPedidos();
        if (res.status === 'success') setPedidos(res.data.pedidos || []);
        else setError(res.message || 'Error al cargar los pedidos');
      } catch { setError('Error de conexión'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtrados = pedidos.filter(p => {
    if (!q) return true;
    return String(p.id_pedido).includes(q) || p.estado_nombre?.toLowerCase().includes(q.toLowerCase());
  });

  const columns = [
    {
      title: 'Pedido', key: 'pedido',
      render: (_, p) => (
        <div>
          <div style={{ fontWeight: 600 }}>#{p.id_pedido}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{p.total_items} {p.total_items === 1 ? 'item' : 'items'}</Text>
        </div>
      ),
    },
    {
      title: 'Estado', key: 'estado',
      render: (_, p) => <EstadoPedidoChip nombre={p.estado_nombre} descripcion={p.estado_desc} color={p.estado_color} />,
    },
    {
      title: 'Pago', key: 'pago',
      render: (_, p) => <EstadoPagoChip nombre={p.estado_pago_nombre} descripcion={p.estado_pago_desc} color={p.estado_pago_color} />,
    },
    {
      title: 'Fecha', key: 'fecha',
      render: (_, p) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CalendarOutlined style={{ color: '#aaa', fontSize: 13 }} />
          <Text style={{ fontSize: 13 }}>{fmtFecha(p.fecha_pedido)}</Text>
        </span>
      ),
    },
    {
      title: 'Total', dataIndex: 'total', key: 'total', align: 'right',
      render: (v) => <strong>{fmtMoneda(v)}</strong>,
    },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', minHeight: '60vh', padding: '40px 16px' }}>
      <Title level={4} style={{ marginBottom: 4 }}>Mis Pedidos</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Hacé clic en un pedido para ver su detalle.
      </Text>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {pedidos.length === 0 ? (
        <Empty description="No tenés pedidos realizados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Input
            prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            placeholder="Buscar pedido por número o estado..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            allowClear
            style={{ marginBottom: 16, maxWidth: 400 }}
          />
          <Table
            columns={columns}
            dataSource={filtrados}
            rowKey="id_pedido"
            size="middle"
            onRow={(p) => ({ onClick: () => navigate(clientPath(`/mis-pedidos/${p.id_pedido}`)), style: { cursor: 'pointer' } })}
            pagination={{ pageSize: 15, showSizeChanger: false }}
          />
        </>
      )}
    </div>
  );
};
